import { Inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { Subject, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { KeyboardShortcutName } from '../../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { InteractionMode } from '../../../../../team-apis/core/interaction-modes';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import {
  KeyboardShortcutEventInfo,
  RootKeyboardShortcutService as RootKeyboardShortcutService,
} from '../../../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { InteractionModeService } from './interaction-mode.service';

/** This service allows listening to keyboard shortcut events at the annotation
 * level. Events are emitted only when the current output is the selected
 * output. For objectless projects, all events triggered while the current
 * output is focused are emitted. See RootKeyboardShortcutService to handle/listen
 * all events.  */
@Injectable()
export class OutputKeyboardShortcutService {
  /** @deprecated Use the root keyboard shortcut service events instead, only
   * for objectless projects */
  private event$ = new Subject<KeyboardShortcutEventInfo>();

  constructor(
    private interactionModeService: InteractionModeService,
    private keyboardShortcutService: RootKeyboardShortcutService,
    private store: AbstractAnnotationStore,
    private taskWorkspaceStore: TaskWorkspaceStore,
    @Inject(outputInfoStoreToken) private outputInfoStore: Store<OutputInfo>,
  ) {}

  getEvent$(shortcutName: KeyboardShortcutName) {
    if (this.taskWorkspaceStore.isUsingObjects()) {
      return this.getEventFromRoot$(shortcutName);
    }

    return this.getEventObjectless$(shortcutName);
  }

  /** @deprecated The root keyboard shortcut service already handles all
   * keyboard events, this is only for objectless projects */
  handleKeyboardEvent(event: KeyboardEvent) {
    this.keyboardShortcutService.handleKeyboardEvent(event, this.event$);
  }

  private getEventFromRoot$(shortcutName: KeyboardShortcutName) {
    return this.getEventFromRootForAllOutputs$(shortcutName).pipe(
      filter(() => this.isSelectedOutput()),
    );
  }

  private getEventFromRootForAllOutputs$(shortcutName: KeyboardShortcutName) {
    return this.keyboardShortcutService.event$.pipe(
      filter(
        (event) =>
          event.shortcutName === shortcutName &&
          this.interactionModeMatches(event) &&
          this.store.state().status === 'ready',
      ),
    );
  }

  private interactionModeMatches(shortcut: {
    allowedInteractionModes: InteractionMode[];
  }) {
    return shortcut.allowedInteractionModes.includes(
      this.interactionModeService.interactionMode,
    );
  }

  /** @deprecated Only used for objectless projects, see getEvent$ */
  private getEventObjectless$(shortcutName: KeyboardShortcutName) {
    const isInSplitView$ = this.taskWorkspaceStore(
      'splitViewOutputName',
    ).$.pipe(map(isDefined));
    const outputEvents$ = this.event$.pipe(
      filter(
        (event) =>
          event.shortcutName === shortcutName &&
          this.interactionModeMatches(event),
      ),
    );

    const rootEvents$ = this.getEventFromRootForAllOutputs$(shortcutName).pipe(
      filter(
        (event) =>
          this.isSelectedOutput() ||
          (this.isSplitViewOutput() &&
            this.isShapeListShortcut(event.shortcutName)),
      ),
    );

    return isInSplitView$.pipe(
      switchMap((isInSplitView) =>
        isInSplitView ? rootEvents$ : outputEvents$,
      ),
    );
  }

  private isSelectedOutput() {
    return (
      this.outputInfoStore.state().output.name ===
      this.taskWorkspaceStore.state().selectedOutputName
    );
  }

  private isSplitViewOutput() {
    return (
      this.taskWorkspaceStore.state().splitViewOutputName ===
      this.outputInfoStore.state().output.name
    );
  }

  private isShapeListShortcut(shortcutName: KeyboardShortcutName) {
    return (
      shortcutName === 'deleteShapes' ||
      shortcutName === 'selectAllShapes' ||
      shortcutName === 'selectNextAnnotation' ||
      shortcutName === 'selectPreviousAnnotation'
    );
  }
}
